import { useEffect, useMemo, useState } from "react";
import { FreeNumberInput } from "@/components/ui/free-number-input";
import { Sliders, FloppyDisk, ArrowCounterClockwise, Eye, EyeSlash, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import InfoTip from "@/components/InfoTip";

// All thresholds, grouped + with friendly labels + InfoTip blurbs.
// "kind" determines input rendering: number | usd_cents | percent | hours | days | enum | bool | chars
const SCHEMA = [
  {
    group: "system", title: "System & limits", emoji: "⚙️",
    items: [
      { key: "subscriber_bookmark_cap", label: "Subscriber bookmark cap (per silo)", kind: "number", min: 0, max: 100, info: "Max real bookmarks a subscriber can keep per silo. Default 1 — drives upgrade-to-creator funnel." },
      { key: "creator_bookmark_cap", label: "Creator bookmark cap", kind: "number", min: -1, max: 1000, info: "Max bookmarks a creator can have per silo. -1 = unlimited (default)." },
      { key: "resend_per_creator_per_day", label: "Resend daily rate limit per creator", kind: "number", min: 0, max: 100000, info: "How many emails (DMs, invites, etc.) a single creator can send per day. Default 1000." },
    ],
  },
  {
    group: "ghost_report", title: "Ghost Report (display settings)", emoji: "👻",
    items: [
      { key: "ghost_report_enabled", label: "Ghost Report enabled (global kill-switch)", kind: "bool", info: "Master toggle. When OFF, the Ghost Report widget hides everywhere — Creator dashboard, fan portal, all surfaces. Use to A/B-test the entire feature." },
      { key: "ghost_report_floor_cents", label: "Visibility floor — min projected $/mo to show numbers", kind: "usd_cents", min: 0, max: 10000, info: "Below this projected monthly residual, the widget switches to neutral count framing ('3 people you shared with are exploring My Silo') instead of pathetic dollar figures. Default $10/mo." },
      { key: "ghost_report_attribution_window_days", label: "Rolling attribution window (per referral)", kind: "days", min: 7, max: 90, info: "Each referred user has their own 30-day clock from first-touch. After that, unconverted soft attributions expire (no retroactive credit). Default 30 days per locked decision #1." },
      // Feb 2026 — the affiliate MONEY dials (base rev share %, affiliate gate $)
      // were removed from this panel. They now live ONLY in Billing & Earnings
      // (PricingLab), which is the single source of truth for money. This panel
      // keeps just the Ghost Report's display/behavior knobs.
    ],
  },
];

const fmt$ = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

export default function AdminThresholdsPanel() {
  const [server, setServer] = useState(null); // {thresholds, defaults, creator_overridable}
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/thresholds");
      setServer(r.data);
      setDraft(r.data.thresholds);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load thresholds.");
    }
  };

  useEffect(() => { load(); }, []);

  const dirtyKeys = useMemo(() => {
    if (!server) return [];
    return Object.keys(draft).filter((k) => draft[k] !== server.thresholds[k]);
  }, [draft, server]);

  const saveAll = async () => {
    if (!dirtyKeys.length) return;
    setBusy(true);
    try {
      const updates = Object.fromEntries(dirtyKeys.map((k) => [k, draft[k]]));
      const r = await api.patch("/admin/thresholds", { updates });
      setServer({ ...server, thresholds: r.data.thresholds });
      setDraft(r.data.thresholds);
      toast.success(`Saved ${dirtyKeys.length} threshold change${dirtyKeys.length === 1 ? "" : "s"}.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };

  const resetGroup = async (group) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/thresholds/reset", { group });
      setServer({ ...server, thresholds: r.data.thresholds });
      setDraft(r.data.thresholds);
      toast.success(`${group} thresholds reset to defaults.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed."); }
    finally { setBusy(false); }
  };

  const resetAll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/thresholds/reset", {});
      setServer({ ...server, thresholds: r.data.thresholds });
      setDraft(r.data.thresholds);
      toast.success("All thresholds reset to platform defaults.");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed."); }
    finally { setBusy(false); }
  };

  if (!server) return <div className="text-sm text-[#6B6A68]">Loading thresholds…</div>;

  return (
    <div className="space-y-6" data-testid="admin-thresholds-panel">
      {/* Header w/ master save + master reset */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border hairline rounded-lg p-4">
        <div>
          <h2 className="font-serif-display text-2xl flex items-center gap-2">
            <Sliders size={20} weight="duotone" className="text-[#4A5D4E]" />
            Platform Limits & Thresholds
          </h2>
          <p className="text-xs text-[#6B6A68] mt-1 max-w-xl">
            Master control over every quota, cap, and behavior gate in My Silo. Each setting has a per-category Reset and an InfoTip explainer.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <span className="text-[11px] text-[#6B6A68]">{dirtyKeys.length ? `${dirtyKeys.length} unsaved` : "All saved"}</span>
          <button onClick={saveAll} disabled={busy || !dirtyKeys.length}
            data-testid="thresholds-save-all"
            className="text-sm px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-40 flex items-center gap-1.5">
            <FloppyDisk size={14} weight="duotone" /> Save all
          </button>
        </div>
      </div>

      {SCHEMA.map((group) => (
        <ThresholdGroupCard
          key={group.group}
          group={group}
          values={draft}
          defaults={server.defaults}
          onChange={(k, v) => setDraft((d) => ({ ...d, [k]: v }))}
          onResetGroup={() => resetGroup(group.group)}
          dirty={dirtyKeys}
          busy={busy}
        />
      ))}
    </div>
  );
}

function ThresholdGroupCard({ group, values, defaults, onChange, onResetGroup, dirty, busy }) {
  // Collapsible accordion (Q1c). Expand by default only if there are unsaved
  // changes in this group — keeps the page scannable even with many groups.
  const groupHasDirty = group.items.some((it) => dirty.includes(it.key));
  const [open, setOpen] = useState(groupHasDirty);
  // If new dirty rows appear (e.g. Reset All fires), auto-open the group so the
  // user can see what's affected.
  useEffect(() => { if (groupHasDirty) setOpen(true); }, [groupHasDirty]);
  const dirtyCount = group.items.filter((it) => dirty.includes(it.key)).length;
  return (
    <div className="bg-white border hairline rounded-lg" data-testid={`thresholds-group-${group.group}`}>
      <div className="flex items-center justify-between px-5 py-3">
        <button onClick={() => setOpen((o) => !o)} data-testid={`thresholds-group-toggle-${group.group}`}
          className="flex items-center gap-2 text-left">
          {open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
          <span className="font-serif-display text-xl flex items-center gap-2">
            <span>{group.emoji}</span>{group.title}
          </span>
          <span className="text-[10px] text-[#6B6A68] uppercase tracking-[0.18em]">
            {group.items.length} setting{group.items.length === 1 ? "" : "s"}
            {dirtyCount > 0 && <span className="text-[#C16E5A] ml-1">· {dirtyCount} unsaved</span>}
          </span>
        </button>
        <button onClick={onResetGroup} disabled={busy}
          data-testid={`thresholds-reset-${group.group}`}
          className="text-[11px] px-3 py-1.5 rounded-md border hairline text-[#6B6A68] hover:border-[#C16E5A] hover:text-[#C16E5A] disabled:opacity-40 flex items-center gap-1">
          <ArrowCounterClockwise size={11} /> Reset {group.title.toLowerCase()}
        </button>
      </div>
      {open && (
        <div className="border-t hairline px-5 py-4 space-y-3">
          {group.items.map((item) => (
            <ThresholdRow key={item.key} item={item}
              value={values[item.key]} defaultValue={defaults[item.key]}
              isDirty={dirty.includes(item.key)}
              onChange={(v) => onChange(item.key, v)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ThresholdRow({ item, value, defaultValue, isDirty, onChange }) {
  return (
    <div className={`flex items-start gap-3 p-3 rounded-md ${isDirty ? "bg-[#FFF9E6]" : ""}`} data-testid={`thresholds-row-${item.key}`}>
      <div className="flex-1 min-w-0 pt-1.5">
        <div className="flex items-center gap-1.5 text-sm font-medium text-[#1C1B1A]">
          {item.label}
          <InfoTip placement="top-right" width={300}>{item.info}</InfoTip>
          {isDirty && <span className="text-[10px] text-[#C16E5A] ml-auto">unsaved</span>}
        </div>
        <div className="text-[10px] text-[#6B6A68]">
          Default: <span className="font-mono">{renderDisplay(defaultValue, item)}</span>
        </div>
      </div>
      <div className="shrink-0">
        <ThresholdInput item={item} value={value} onChange={onChange} />
      </div>
    </div>
  );
}

function ThresholdInput({ item, value, onChange }) {
  const cls = "bg-white border hairline rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] w-32";
  switch (item.kind) {
    case "number":
    case "hours":
    case "days":
    case "percent":
      return <FreeNumberInput min={item.min} max={item.max} step={1}
        value={value ?? 0} onChange={(v) => onChange(v)}
        data-testid={`thresholds-input-${item.key}`} className={cls + " tabular-nums"} />;
    case "usd_cents":
      return <div className="flex items-center gap-1">
        <span className="text-[#6B6A68]">$</span>
        <FreeNumberInput min={item.min/100} max={item.max/100} step={1}
          value={Math.round((value ?? 0) / 100)}
          onChange={(v) => onChange(v * 100)}
          data-testid={`thresholds-input-${item.key}`} className={cls + " tabular-nums"} />
      </div>;
    case "chars":
      return <FreeNumberInput min={item.min} max={item.max} step={1000}
        value={value ?? 0} onChange={(v) => onChange(v)}
        data-testid={`thresholds-input-${item.key}`} className={cls + " tabular-nums"} />;
    case "enum":
      return <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        data-testid={`thresholds-input-${item.key}`} className={cls + " w-44"}>
        {item.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>;
    case "bool":
      return <button onClick={() => onChange(!value)} data-testid={`thresholds-input-${item.key}`}
        className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 ${value ? "bg-[#4A5D4E] text-white" : "bg-[#F0F1EE] text-[#6B6A68]"}`}>
        {value ? <Eye size={12} /> : <EyeSlash size={12} />} {value ? "ON" : "OFF"}
      </button>;
    default:
      return <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cls} />;
  }
}

function renderDisplay(v, item) {
  if (item.kind === "usd_cents") return fmt$(v);
  if (item.kind === "percent") return `${v}%`;
  if (item.kind === "hours") return `${v}h`;
  if (item.kind === "days") return `${v}d`;
  if (item.kind === "bool") return v ? "ON" : "OFF";
  if (item.kind === "chars") return `${(v / 1000).toFixed(0)}K`;
  if (typeof v === "string") return v;
  return v == null ? "—" : String(v);
}
